/**
 * ConflictDialog - shown when the server refuses a write because the data
 * changed under it: a save whose If-Match is stale (412), or a move whose row
 * or anchor is gone (409). SimulationForm opens it (see `useWriteErrors`).
 *
 * - Refresh: load the current version from the server
 * - Retry (only when `onRetry` is given): attempt the write anyway
 */

import { AlertTriangle, RefreshCcw, ArrowRight } from 'lucide-react';
import { Dialog } from '@/components/dialog/Dialog';
import { DialogActions } from '@/components/dialog/DialogActions';
import { Button } from '@lean-agility/design-system/react';

export interface ConflictDialogProps {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;
  /**
   * Callback when the dialog is closed.
   */
  onClose: () => void;
  /**
   * Callback when user chooses to refresh data from server.
   */
  onRefresh: () => void;
  /**
   * Callback when user chooses to retry the operation anyway.
   * If not provided, the retry button is hidden.
   */
  onRetry?: () => void;
  /**
   * Optional resource type for more specific messaging.
   */
  resourceType?: 'team' | 'feature' | 'deliverable' | 'simulation';
  /**
   * Whether a refresh operation is in progress.
   */
  isRefreshing?: boolean;
}

const RESOURCE_LABELS: Record<string, string> = {
  team: 'team',
  feature: 'feature',
  deliverable: 'deliverable',
  simulation: 'simulation',
};

export function ConflictDialog({
  isOpen,
  onClose,
  onRefresh,
  onRetry,
  resourceType,
  isRefreshing = false,
}: ConflictDialogProps) {
  if (!isOpen) return null;

  const resourceLabel = resourceType ? RESOURCE_LABELS[resourceType] : 'resource';

  return (
    <Dialog title="Update Conflict" onClose={onClose} maxWidth="max-w-lg">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div
            className="
              shrink-0 rounded-full bg-amber-50 p-2
              dark:bg-amber-900/20
            "
          >
            <AlertTriangle className="size-5 text-feedback-warning" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-text-primary">This {resourceLabel} has been modified</p>
            <p className="text-sm text-text-secondary">
              Someone else updated this {resourceLabel} since you loaded it. Your changes could not be saved to prevent
              overwriting their work.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-bg-subtle p-3 text-sm">
          <p className="font-medium text-text-primary">What would you like to do?</p>
          <ul className="mt-2 space-y-1 text-text-secondary">
            <li>
              <span className="font-medium text-text-primary">Refresh</span> - Load the latest version and apply your
              changes again
            </li>
            {onRetry && (
              <li>
                <span className="font-medium text-text-primary">Retry</span> - Attempt to save your changes anyway (may
                fail again)
              </li>
            )}
          </ul>
        </div>
      </div>

      <DialogActions>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            <ArrowRight className="mr-2 size-4" />
            Retry
          </Button>
        )}
        <Button variant="primary" onClick={onRefresh} disabled={isRefreshing}>
          {isRefreshing ? (
            <>
              <RefreshCcw className="mr-2 size-4 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCcw className="mr-2 size-4" />
              Refresh
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
